import React, { useState, useMemo, useEffect } from "react";
import PageLayout from "../../Layouts/PageLayout/PageLayout";
import TableWithFilters from "../../components/filtersNew/table-with-filtersNew";
import Button from "../../components/common/Button/Button";
import { FiDownload } from "react-icons/fi";
import "./ObrasMenores.scss";
import { getApiInstance } from "../../utils/api";
import { CiCircleCheck, CiClock1 } from "react-icons/ci";
import { CgDanger } from "react-icons/cg";
import { useZone } from "../../context/ZoneContext";
import { mapList } from "../../utils/utils";
import { useTranslation } from "react-i18next";
import { t } from "i18next";

const columnasObrasMenores = [
    { key: "codObraMenor", label: "ID", visible: false },
    {
        key: "nombre",
        label: t("minorWorks.columns.centerCode"),
        visible: true,
        sortable: true,
        isLink: true,
        width: "130px",
        visibleOnExpand: true,
    },
    {
        key: "localidad",
        label: t("fields.locality"),
        visible: true,
        sortable: true,
        width: "200px",
        visibleOnExpand: true,
    },
    {
        key: "fechaFin",
        label: t("fields.endDate"),
        visible: true,
        sortable: true,
        type: "date",
        width: "120px",
        visibleOnExpand: true,
    },
    {
        key: "intermediario",
        label: t("minorWorks.columns.intermediaryCompany"),
        visible: true,
        sortable: true,
        width: "180px",
        visibleOnExpand: true,
    },
    {
        key: "cadena",
        label: t("fields.chain"),
        visible: true,
        sortable: true,
        width: "120px",
        visibleOnExpand: true,
    },
    {
        key: "codEstado",
        label: t("fields.state"),
        visible: true,
        sortable: true,
        width: "120px",
        visibleOnExpand: true,
    },
];

const estadosBadges = [
    { label: "states.noData", type: "info", Icon: CiClock1 },
    { label: "states.pending", type: "info", Icon: CiClock1 },
    { label: "states.incident", type: "warning", Icon: CgDanger },
    { label: "states.validated", type: "success", Icon: CiCircleCheck },
    { label: "Cerrada", type: "primary", Icon: CiCircleCheck },
];

const filterFields = [
    {
        key: "nombre",
        label: "minorWorks.columns.centerCode",
        type: "multiselect",
    },
    { key: "localidad", label: "fields.locality", type: "multiselect" },
    {
        key: "intermediario",
        label: "minorWorks.columns.intermediaryCompany",
        type: "multiselect",
    },
    { key: "fechaFin", label: "fields.endDate", type: "date" },
    { key: "cadena", label: "fields.chain", type: "multiselect" },
    {
        key: "codEstado",
        label: "fields.state",
        type: "cards",
        labelMap: estadosBadges.map((item) => item.label),
    },
];

export default function ObrasMenores({ user }) {
    const { t } = useTranslation();
    const [obrasMenores, setObrasMenores] = useState();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { selectedZone } = useZone();
    const [productores, setProductores] = useState([]);
    const [comunidades, setComunidades] = useState([]);
    const [paises, setPaises] = useState([]);
    const [provincias, setProvincias] = useState([]);
    const [usuariosIntermediarios, setUsuariosIntermediarios] = useState([]);
    const [cadenas, setCadenas] = useState([]);

    const estadosBadges = [
        { label: "Sin datos", type: "info", Icon: CiClock1 },
        { label: "Pendiente", type: "info", Icon: CiClock1 },
        { label: "Incidencia", type: "warning", Icon: CgDanger },
        { label: "Validada", type: "success", Icon: CiCircleCheck },
        { label: "Cerrada", type: "primary", Icon: CiCircleCheck },
    ];

    const filterFields = [
        { key: "nombre", label: "Código Centro", type: "multiselect" },
        { key: "localidad", label: "Localidad", type: "multiselect" },
        {
            key: "intermediario",
            label: "Empresa Intermediaria",
            type: "multiselect",
        },
        { key: "fechaFin", label: "Fecha fin", type: "date" },
        { key: "cadena", label: "Cadena", type: "multiselect" },
        {
            key: "codEstado",
            label: "Estado",
            type: "cards",
            labelMap: estadosBadges.map((item) => item.label),
        },
    ];

    const fetchObrasMenores = async () => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/ObraMenor/GetObrasMenoresFromUser/${selectedZone.value}`
            );

            setObrasMenores(response.data);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las obras. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchUsuariosIntermediarios = async () => {
        const api = getApiInstance();
        try {
            let usuariosIntermediariosData = [];

            const response = await api.get(
                `/api/Usuario/GetAllUsuariosIntermediarios/${selectedZone.value}`
            );
            usuariosIntermediariosData = response.data;

            setUsuariosIntermediarios(
                mapList(
                    usuariosIntermediariosData,
                    ["nombre", "apellidos"],
                    "codUsuario",
                    "codIntermediario",
                    "codIntermediario"
                )
            );
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCadenas = async () => {
        const api = getApiInstance();
        try {
            let cadenasData = [];

            const response = await api.get(`/api/Cadenas/GetAllCadenas`);
            cadenasData = response.data;

            setCadenas([
                ...mapList(cadenasData, "cadena", "codCadena", "codCadena"),
            ]);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchComunidades = async () => {
        const api = getApiInstance();

        try {
            let comunidadesData = [];

            const response = await api.get(
                `/api/Comunidades/GetComunidadesByZona/${selectedZone.value}`
            );
            comunidadesData = response.data;

            setComunidades(
                mapList(
                    comunidadesData,
                    "comunidad",
                    "codComunidad",
                    "codComunidad",
                    "codPais"
                )
            );

            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las comunidades. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchPaises = async () => {
        const api = getApiInstance();

        try {
            let paisesData = [];

            const response = await api.get(
                `/api/Paises/GetPaisesByZona/${selectedZone.value}`
            );
            paisesData = response.data;

            setPaises(mapList(paisesData, "pais", "codPais", "codPais"));
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar los países. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchProvincias = async () => {
        const api = getApiInstance();
        try {
            let provinciasData = [];

            const response = await api.get(
                `/api/Provincias/GetProvinciasByZona/${selectedZone.value}`
            );
            provinciasData = response.data;

            setProvincias(
                mapList(
                    provinciasData,
                    "provincia",
                    "codProvincia",
                    "codProvincia",
                    "codComunidad"
                )
            );
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchProductores = async () => {
        const api = getApiInstance();
        try {
            let productoresData = [];

            const response = await api.get(
                `/api/Productores/GetProductoresByZona/${selectedZone.value}`
            );
            productoresData = response.data;

            setProductores([
                ...mapList(productoresData, "nombre", "codProductor"),
            ]);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);

        const loadAll = async () => {
            try {
                await fetchObrasMenores();

                await Promise.all([
                    fetchUsuariosIntermediarios(),
                    fetchCadenas(),
                    fetchPaises(),
                    fetchComunidades(),
                    fetchProvincias(),
                    fetchProductores(),
                ]);
            } catch (err) {
                if (isMounted) {
                    setError(
                        "No se han podido cargar los datos. Vuelve a intentarlo más tarde."
                    );
                }
                console.error("Error en loadAll:", err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadAll();

        return () => {
            isMounted = false;
        };
    }, [selectedZone]);

    return (
        <PageLayout
            title={t("minorWorks.title")}
            subtitle={t("minorWorks.subtitle")}
            actions={[
                <Button
                    key="export"
                    componentType="link"
                    variant="outlined"
                    href="/"
                    startIcon={<FiDownload />}
                >
                    {t("common.export")}
                </Button>,
                <Button
                    key="import"
                    componentType="link"
                    variant="outlined"
                    href="/"
                    startIcon={<FiDownload />}
                >
                    {t("common.import")}
                </Button>,
            ]}
        >
            <div className="obras-menores-table">
                <TableWithFilters
                    quickFields={["intermediario", "fechaFin", "codEstado", "nombre"]}
                    columns={columnasObrasMenores}
                    dataTable={obrasMenores}
                    showCheckboxes={true}
                    expandable={true}
                    noResultsMessage={t("minorWorks.noWorksFound")}
                    actions={[t("common.delete"), t("common.edit")]}
                    searchTerms={[
                        t("minorWorks.columns.centerCode"),
                        t("fields.locality"),
                        t("minorWorks.columns.intermediaryCompany"),
                        t("fields.chain"),
                        t("fields.state"),
                    ]}
                    keyProp="codObraMenor"
                    states={estadosBadges}
                    stateProp="codEstado"
                    elementName={t("minorWorks.title")}
                    sortProp="nombre"
                    filterFields={filterFields}
                    loading={loading}
                    formElements={[
                        {
                            title: t("sections.generalInfo"),
                            titleVisible: true,
                            elements: [
                                {
                                    name: "codProductor",
                                    type: "select",
                                    label: t("fields.producer"),
                                    placeholder: t("placeholders.selectDate"),
                                    required: false,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                    options: productores,
                                },
                                {
                                    name: "nombre",
                                    type: "text",
                                    label: t("minorWorks.columns.centerCode"),
                                    placeholder: t("placeholders.enterName"),
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                },
                                {
                                    name: "fechaInicio",
                                    type: "date",
                                    label: t("fields.startDate"),
                                    placeholder: t("placeholders.selectDate"),
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                },
                                {
                                    name: "fechaFin",
                                    type: "date",
                                    label: t("fields.endDate"),
                                    placeholder: t("placeholders.selectDate"),
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                },
                                {
                                    name: "CodUsuario",
                                    type: "select",
                                    label: t("fields.intermediary"),
                                    placeholder: t("placeholders.selectDate"),
                                    required: false,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                    options: usuariosIntermediarios,
                                },
                                {
                                    name: "codCadena",
                                    type: "select",
                                    label: t("fields.chain"),
                                    placeholder: t("placeholders.selectDate"),
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                    options: cadenas,
                                },
                                {
                                    name: "obs",
                                    type: "textarea",
                                    label: t("fields.observations"),
                                    placeholder: t(
                                        "minorWorks.form.observationsPlaceholder"
                                    ),
                                    required: false,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                },
                            ],
                        },
                        {
                            title: t("sections.location"),
                            titleVisible: true,
                            elements: [
                                {
                                    name: "direccion",
                                    type: "text",
                                    label: t("fields.address"),
                                    placeholder: t("placeholders.enterAddress"),
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                },
                                {
                                    name: "codPais",
                                    type: "select",
                                    label: t("fields.country"),
                                    placeholder: t("placeholders.enterCountry"),
                                    options: paises,
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                },
                                {
                                    name: "codComunidad",
                                    type: "select",
                                    label: t("fields.community"),
                                    placeholder: t(
                                        "placeholders.enterCommunity"
                                    ),
                                    options: comunidades,
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                    associatedFormElement: "codPais",
                                },
                                {
                                    name: "codProvincia",
                                    type: "select",
                                    label: t("fields.province"),
                                    placeholder: t(
                                        "placeholders.enterProvince"
                                    ),
                                    options: provincias,
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                    associatedFormElement: "codComunidad",
                                },
                                {
                                    name: "localidad",
                                    type: "text",
                                    label: t("fields.locality"),
                                    placeholder: t(
                                        "placeholders.enterLocality"
                                    ),
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                },
                                {
                                    name: "codZona",
                                    type: "number",
                                    label: "Zona",
                                    visible: false,
                                    defaultValue: selectedZone.value,
                                },
                            ],
                        },
                        {
                            title: t("minorWorks.form.generatedQuantities"),
                            titleVisible: true,
                            elements: [
                                {
                                    name: "cantidadRP",
                                    type: "number",
                                    label: t("minorWorks.form.dangerousWaste"),
                                    placeholder: t("placeholders.enterName"),
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                },
                                {
                                    name: "cantidadRNP",
                                    type: "number",
                                    label: t(
                                        "minorWorks.form.nonDangerousWaste"
                                    ),
                                    placeholder: t("placeholders.enterName"),
                                    required: true,
                                    disabled: false,
                                    labelVisible: true,
                                    visible: true,
                                },
                            ],
                        },
                    ]}
                    addApiUrl="/api/ObraMenor/SetObraMenor"
                    editApiUrl="/api/ObraMenor/UpdateObraMenor"
                    deleteApiUrl="/api/ObraMenor/DeleteObraMenor"
                    userCodAcceso={Number(user.codAcceso)}
                    userCodPerfil={Number(user.role)}
                />
            </div>
        </PageLayout>
    );
}
